import { c as _c } from "react/compiler-runtime";
import { Select } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { useBuilder } from '../../store/provider';
import type { DecisionNode, DecisionStateRef } from '../../store/types';
import { isDecisionNode, isManagedNode, isRulesNode, isTaskNode } from '../../store/types';
import { uid } from '../../store/utils';
type StateRefsProps = {
  node: DecisionNode;
};
type RefChoice = Pick<DecisionStateRef, 'source' | 'key'>;
const EVERYTHING = 'all';
const refToken = ({
  source,
  key
}: RefChoice): string => `${source}s.${key}`;

// One value or everything. The graph allows several refs (an API author can set them), so a node
// that arrives that way shows a summary option until the author picks again.
const StateRefs = t0 => {
  const $ = _c(50);
  const {
    node
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision.state"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const {
    nodes,
    inputs
  } = state.bundle;
  let choices;
  let t2;
  let value;
  if ($[1] !== inputs || $[2] !== node.id || $[3] !== node.state || $[4] !== nodes || $[5] !== t) {
    let t3;
    if ($[9] !== node.id) {
      t3 = candidate => candidate.id === node.id;
      $[9] = node.id;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    const earlier = nodes.slice(0, nodes.findIndex(t3));
    choices = [...inputs.filter(_temp).map(_temp2), ...earlier.flatMap(_temp7)];
    let t4;
    if ($[11] !== node.state) {
      t4 = node.state.length > 1 ? node.state.map(refToken).join(", ") : null;
      $[11] = node.state;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const several = t4;
    let t5;
    if ($[13] !== node.state[0] || $[14] !== node.state.length || $[15] !== several) {
      t5 = node.state.length === 0 ? EVERYTHING : several ?? refToken(node.state[0]);
      $[13] = node.state[0];
      $[14] = node.state.length;
      $[15] = several;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    value = t5;
    let t6;
    if ($[17] !== t) {
      t6 = t(_temp8);
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t6) {
      t7 = {
        value: EVERYTHING,
        label: t6
      };
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== several) {
      t8 = several ? [{
        value: several,
        label: several
      }] : [];
      $[21] = several;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    t2 = [t7, ...t8, ...choices.map(_temp9)];
    $[1] = inputs;
    $[2] = node.id;
    $[3] = node.state;
    $[4] = nodes;
    $[5] = t;
    $[6] = choices;
    $[7] = t2;
    $[8] = value;
  } else {
    choices = $[6];
    t2 = $[7];
    value = $[8];
  }
  const options = t2;
  let t3;
  if ($[23] !== actions || $[24] !== choices || $[25] !== node.id) {
    t3 = next => {
      if (next === EVERYTHING) {
        actions.decision.setStateRefs(node.id, []);
        return;
      }
      const choice_0 = choices.find(candidate_1 => refToken(candidate_1) === next);
      if (choice_0) {
        actions.decision.setStateRefs(node.id, [{
          id: uid("sref"),
          ...choice_0
        }]);
      }
    };
    $[23] = actions;
    $[24] = choices;
    $[25] = node.id;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  const handleChange = t3;
  let t4;
  if ($[27] !== t) {
    t4 = t(_temp10);
    $[27] = t;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== t4) {
    t5 = <span className="shrink-0 text-body-3 text-tertiary">{t4}</span>;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const t6 = `decision-${node.id}-state`;
  let t7;
  if ($[31] !== t) {
    t7 = t(_temp11);
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== handleChange || $[34] !== isReadOnly || $[35] !== options || $[36] !== t6 || $[37] !== t7 || $[38] !== value) {
    t8 = <Select id={t6} aria-label={t7} size="compact" value={value} options={options} disabled={isReadOnly} onChange={handleChange} />;
    $[33] = handleChange;
    $[34] = isReadOnly;
    $[35] = options;
    $[36] = t6;
    $[37] = t7;
    $[38] = value;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== t5 || $[41] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}{t8}</div>;
    $[40] = t5;
    $[41] = t8;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== t) {
    t10 = t(_temp12);
    $[43] = t;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== t10) {
    t11 = <span className="text-caption-3 text-tertiary">{t10}</span>;
    $[45] = t10;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t11 || $[48] !== t9) {
    t12 = <div className="flex flex-col gap-1.5 rounded border border-tertiary bg-secondary p-2">{t9}{t11}</div>;
    $[47] = t11;
    $[48] = t9;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  return t12;
};
export default StateRefs;
function _temp(input) {
  return input.name.trim();
}
function _temp2(input_0) {
  return {
    source: "input" as const,
    key: input_0.name
  };
}
function _temp3(field) {
  return field.name.trim();
}
function _temp4(field_0) {
  return {
    source: "output",
    key: field_0.name
  };
}
function _temp5(q) {
  return q.key.trim();
}
function _temp6(q_0) {
  return {
    source: "output",
    key: q_0.key
  };
}
function _temp7(candidate_0) {
  const task = isManagedNode(candidate_0) ? candidate_0.task : isTaskNode(candidate_0) ? candidate_0 : null;
  if (task) {
    return task.fields.filter(_temp3).map(_temp4);
  }
  if (isRulesNode(candidate_0)) {
    return candidate_0.key.trim() ? [{
      source: "output",
      key: candidate_0.key
    }] : [];
  }
  if (isDecisionNode(candidate_0)) {
    return candidate_0.questions.filter(_temp5).map(_temp6);
  }
  return [];
}
function _temp8($) {
  return $.everything;
}
function _temp9(choice) {
  return {
    value: refToken(choice),
    label: refToken(choice)
  };
}
function _temp10($_0) {
  return $_0.label;
}
function _temp11($_1) {
  return $_1.ariaLabel;
}
function _temp12($_2) {
  return $_2.hint;
}